import { cn } from '@h2hspark/ui';
import { CheckCircle2, Clock, Lock } from 'lucide-react';
import * as React from 'react';

import { ConnectButton } from '../connections/connect-action-panel';

import {
  ATTENDEE_ABOUT_LABEL,
  ATTENDEE_INTERESTS_LABEL,
  CONNECTED_NOTICE_TITLE,
  GOING_TO_THIS_EVENT_LABEL,
  HERE_TO_TALK_ABOUT_LABEL,
  NO_LONGER_COMING_LABEL,
  REQUEST_SENT_NOTICE_TITLE,
  SAVING_IS_NOT_CONNECTING,
  connectedNoticeDetail,
  fullName,
  initialsOf,
  nothingWrittenYet,
  requestSentNoticeDetail,
  roleAtCompany,
} from './constants';
import { SaveToMeetButton } from './save-to-meet-panel';
import type { AttendeeProfile, FullAttendeeProfile } from './types';

/**
 * **The attendee detail card — `Web App.dc.html` §2.16's `personOpen` overlay** (`MEM-012`,
 * `MEM-025` AC-6, `MEM-032` AC-2, `MEM-056` AC-3).
 *
 * ## Why this is a component and not two screens
 *
 * The prototype has **one** card for a person inside an event, opened from an attendee row, from a
 * match card's head, from a request avatar and from *Request sent*. This repository had two
 * screens instead — the attendee profile and the match detail — each with its own hero, its own
 * section labels and its own pair of full-width buttons, and they had already drifted: the profile
 * used the served `relationship` and the detail still held Connect in a `useState`, so the same
 * person could read *Connected* on one screen and *Connect* on the other. Sumit, reviewing the
 * deployed preview on 2026-09-12: *"attendee profile is not cleaned up"* (ruling 64).
 *
 * One card, two callers. The match detail passes {@link AttendeeDetailCardProps.narrative} and
 * nothing else differs.
 *
 * ## Element by element, against §2.16 (lines 1246–1283)
 *
 * | Element | §2.16 | Here |
 * | --- | --- | --- |
 * | Card | `max-width:460px`, `--surface-page`, `--radius-xl`, `--shadow-xl`, `overflow:hidden` | `max-w-[460px]`, `bg-background`, `rounded-xl`, `shadow-xl` — applied by the caller, which owns whether it is a dialog or a page |
 * | Header | `--spark-gradient`, 28px, centred column | `bg-spark p-7 text-center` |
 * | Close | 34px round, `rgba(255,255,255,.2)`, top/right 16 | the `control` slot — a ✕ in the dialog, the page's floating back link on the route |
 * | Avatar | 76px white disc, `--brand-primary`, `font-brand 800/25px`, `--shadow-md`, mb 14 | `h-[76px] w-[76px] … text-[25px] … mb-3.5` |
 * | Name | `<h3>` `--text-xl`, white, mb 3 | `text-xl` in the caller's heading level |
 * | Role | `--text-sm`, `opacity:.92` | `text-sm opacity-[.92]` |
 * | Body | 24px | `p-6` |
 * | Pill | `--purple-100` on `--purple-700`, `font-brand 700 --text-2xs`, `5px 11px`, mb 20 | `bg-brand-100 text-brand-700` |
 * | Labels | `font-brand 600 --text-2xs`, `.08em`, uppercase, `--text-subtle`, mb 8 | `SECTION_LABEL_CLASS` |
 * | Quote box | `--purple-50`, `--radius-md`, `13px 15px`, `--text-sm`, snug | `bg-brand-50 rounded-md px-[15px] py-[13px]` |
 * | Interests | `--surface-sunken` pills, `font-brand 600 --text-xs`, `6px 11px`, gap 7 | `bg-muted … gap-[7px]` |
 * | Connected box | `--success-bg`, `--radius-md`, 14px, `check-circle-2` | as drawn |
 * | Pending box | `--surface-sunken`, `--radius-md`, 14px, `clock` in `--warning` | as drawn |
 * | Actions | `display:flex; gap:10px`, two `flex:1` buttons | `flex gap-2.5` over the two shared controls in their `detail` variant |
 *
 * ## The three departures, each with its reason
 *
 * 1. **The reduced view.** §2.16 draws the quote, the About and the interests for everybody; its
 *    `registered` flag gates only the buttons. `MEM-012` AC-4 says the reduced view carries no bio,
 *    no interests and no actions, and AC-5 makes that a server-side gate carrying
 *    `security-invariants#member-data-boundary`. The story wins: this branches on the payload's own
 *    `access` discriminant, so a reduced payload has no `bio` and no `interests` to render even by
 *    mistake. The conversation starter stays, as it did before — `MEM-011` AC-6 already shows it to
 *    the same reader on a preview card.
 * 2. **Empty regions are omitted, and one sentence stands in when all three are.** §2.16's fixture
 *    attendee has a quote, a paragraph and three interests. See `nothingWrittenYet`.
 * 3. **`SAVING_IS_NOT_CONNECTING` is under the pair and is not in the prototype.** `MEM-013` AC-3
 *    requires the two states to be *"presented distinctly"*, and with the controls now side by side
 *    and the same size that distinction has nothing else to carry it. **One** quiet line, where
 *    there were two: the second — *"On your meet list — private to you."* — said the same thing
 *    again under a control whose label already said it.
 */
export interface AttendeeDetailCardProps {
  readonly eventId: string;
  /** The profile as served. `reduced` renders `MEM-012` AC-3's locked view. */
  readonly profile: AttendeeProfile;
  /**
   * `MEM-025` AC-6's *"Why you should meet"* — the match narrative and its label, above the
   * quote, on the match detail only. `null` on the attendee detail, which is not a match.
   *
   * The label travels with the text rather than being written here, because it is `matching`'s
   * word for `matching`'s sentence and this slice must not import from that one — the dependency
   * runs the other way, and the match detail is already built out of `attendee-preview`.
   */
  readonly narrative: { readonly label: string; readonly text: string } | null;
  /** The way out, laid over the gradient header: a ✕ in the overlay, a back link on the route. */
  readonly control: React.ReactNode;
  /**
   * `MEM-032` AC-4's pre-filled composer message, or `null`. An attendee detail is not a match and
   * pre-filling *"we matched"* there would be a claim the platform cannot support.
   */
  readonly prefilledMessage: string | null;
  /** `h1` on the route, where the card is the page; `h2` in the overlay, under the dialog's title. */
  readonly headingLevel: 'h1' | 'h2';
  readonly className?: string | undefined;
}

/** §2.16's section label: `font-brand 600/--text-2xs`, `.08em`, uppercase, `--text-subtle`. */
const SECTION_LABEL_CLASS =
  'mb-2 font-brand text-2xs font-semibold uppercase tracking-caps text-text-subtle';

export function AttendeeDetailCard({
  eventId,
  profile,
  narrative,
  control,
  prefilledMessage,
  headingLevel,
  className,
}: AttendeeDetailCardProps): React.ReactElement {
  const Heading = headingLevel;
  const line = roleAtCompany(profile);

  return (
    <div className={cn('overflow-hidden bg-background', className)}>
      {/* §2.16's gradient header. `text-center` and a centred column, 28px all round. */}
      <div className="relative flex flex-col items-center bg-spark p-7 text-center text-white">
        {control}
        <span
          className="mb-3.5 inline-flex h-[76px] w-[76px] items-center justify-center rounded-pill bg-white font-brand text-[25px] font-extrabold text-brand shadow-md"
          aria-hidden
        >
          {initialsOf(profile)}
        </span>
        <Heading className="mb-[3px] font-brand text-xl font-extrabold text-white">
          {fullName(profile)}
        </Heading>
        {line === null ? null : <p className="text-sm opacity-[.92]">{line}</p>}
      </div>

      <div className="p-6">
        {/* §2.16's pill. `MEM-013` AC-5's marked entry says the other thing in the same place,
            because a card that read "Going to this event" about somebody who cancelled would be
            the one sentence on it that is false. */}
        <p className="mb-5 inline-flex rounded-pill bg-brand-100 px-[11px] py-[5px] font-brand text-2xs font-bold text-brand-700">
          {profile.access === 'full' && !profile.stillAttending
            ? NO_LONGER_COMING_LABEL
            : GOING_TO_THIS_EVENT_LABEL}
        </p>

        {/* `MEM-025` AC-6's first section, on the match detail only. Above the person's own words
            because it is why this card was opened, and it is the match set's rather than theirs —
            which is why it keeps its own label instead of being folded into the quote box the way
            §2.16's `a.quote || a.why` folds them. */}
        {narrative === null ? null : (
          <section className="mb-5">
            <h3 className={SECTION_LABEL_CLASS}>{narrative.label}</h3>
            <p className="rounded-md bg-brand-50 px-[15px] py-[13px] text-sm leading-snug text-text-body">
              {narrative.text}
            </p>
          </section>
        )}

        {/* §2.16's "Here to talk about". Served to both views — see the header's note 1. */}
        {profile.conversationStarter === null ? null : (
          <section className="mb-5">
            <h3 className={SECTION_LABEL_CLASS}>{HERE_TO_TALK_ABOUT_LABEL}</h3>
            <p className="rounded-md bg-brand-50 px-[15px] py-[13px] text-sm leading-snug text-text-body">
              {profile.conversationStarter}
            </p>
          </section>
        )}

        {profile.access === 'full' ? (
          <FullDetailBody
            eventId={eventId}
            profile={profile}
            prefilledMessage={prefilledMessage}
          />
        ) : (
          /* `MEM-012` AC-3's route to registration, in the API's own words. The lock is the
             prototype's own treatment for a region a reader is not entitled to. */
          <p className="flex items-start gap-2.5 rounded-md bg-muted p-3.5 text-xs leading-snug text-muted-foreground">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-text-subtle" aria-hidden />
            <span>{profile.registrationPrompt}</span>
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Everything only a registrant is served (`MEM-012` AC-2, AC-4).
 *
 * A separate component so the discriminant narrows once: `FullAttendeeProfile` has the bio, the
 * interests, the meet-list flag and the relationship, and the reduced payload cannot reach here.
 */
function FullDetailBody({
  eventId,
  profile,
  prefilledMessage,
}: {
  readonly eventId: string;
  readonly profile: FullAttendeeProfile;
  readonly prefilledMessage: string | null;
}): React.ReactElement {
  /*
    §2.16 draws all three regions unconditionally; a real member may have written none of them.
    The rule is the connection detail's, settled 2026-09-12: each region renders only when the
    member wrote it, and one sentence stands in when none of them did. A heading over nothing is
    an absence the member did not choose, and a sentence about an absent About beside six
    interests is an apology in the place information goes.
  */
  const wroteNothing =
    profile.conversationStarter === null && profile.bio === null && profile.interests.length === 0;

  return (
    <>
      {profile.bio === null ? null : (
        <section className="mb-5">
          <h3 className={SECTION_LABEL_CLASS}>{ATTENDEE_ABOUT_LABEL}</h3>
          {/* Their own paragraph, never generated — a member reading an invented "About" would
              have no way to know the person did not write it. */}
          <p className="whitespace-pre-line text-sm leading-relaxed text-text-body">
            {profile.bio}
          </p>
        </section>
      )}

      {profile.interests.length === 0 ? null : (
        <section className="mb-[22px]">
          <h3 className={SECTION_LABEL_CLASS}>{ATTENDEE_INTERESTS_LABEL}</h3>
          <ul className="flex flex-wrap gap-[7px]">
            {profile.interests.map((term) => (
              <li
                key={term.termId}
                className="rounded-pill bg-muted px-[11px] py-1.5 font-brand text-xs font-semibold text-text-body"
              >
                {term.displayName}
              </li>
            ))}
          </ul>
        </section>
      )}

      {wroteNothing ? (
        <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
          {nothingWrittenYet(profile.firstName)}
        </p>
      ) : null}

      <RelationshipNotice firstName={profile.firstName} relationship={profile.relationship} />

      {/* §2.16's two half-width buttons, in a 10px row. Both are the shared controls in their
          `detail` treatment, so the roster row, the match card and this card cannot say three
          different things about one person (`MEM-013` AC-2, `MEM-032` AC-2, ruling 65). */}
      <div className="flex gap-2.5">
        <SaveToMeetButton
          eventId={eventId}
          attendeeRef={profile.attendeeRef}
          saved={profile.savedToMeetList}
          variant="detail"
        />
        <ConnectButton
          eventId={eventId}
          attendeeRef={profile.attendeeRef}
          person={profile}
          prefilledMessage={prefilledMessage}
          relationship={profile.relationship}
          connectionRef={profile.connectionRef}
          variant="detail"
        />
      </div>

      {/* `MEM-013` AC-3, once. See the component header's note 3. */}
      <p className="mt-3 text-xs leading-snug text-text-subtle">{SAVING_IS_NOT_CONNECTING}</p>
    </>
  );
}

/** §2.16's connected and pending boxes. Neither is drawn where the reader has sent nothing. */
function RelationshipNotice({
  firstName,
  relationship,
}: {
  readonly firstName: string;
  readonly relationship: FullAttendeeProfile['relationship'];
}): React.ReactElement | null {
  if (relationship === 'none') return null;

  const connected = relationship === 'connected';
  return (
    <div
      className={cn(
        'mb-[18px] rounded-md p-3.5',
        connected ? 'bg-success-subtle' : 'bg-muted',
      )}
    >
      {/* `text-foreground`, not `text-text-strong`: §2.16 names the token `--text-strong` and the
          preset's key for it is `foreground`. The other spelling compiles to nothing and the line
          would inherit its colour — caught by `no-class-names-a-token-that-does-not-exist.test.ts`,
          the guard #536 widened after finding the same mistake in twenty files. */}
      <p className="mb-2 flex items-center gap-2 font-brand text-sm font-bold text-foreground">
        {connected ? (
          <CheckCircle2 className="h-[15px] w-[15px] shrink-0 text-success" aria-hidden />
        ) : (
          <Clock className="h-[15px] w-[15px] shrink-0 text-warning" aria-hidden />
        )}
        {connected ? CONNECTED_NOTICE_TITLE : REQUEST_SENT_NOTICE_TITLE}
      </p>
      <p className="text-xs leading-snug text-muted-foreground">
        {connected ? connectedNoticeDetail(firstName) : requestSentNoticeDetail(firstName)}
      </p>
    </div>
  );
}
